import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { muscleLabel, focusLevelLabel } from 'components/play/constants';

/**
 * OTRIX Play — lista de enfoque muscular de la semana. Acompaña al BodyMap: traduce la
 * distribución de la rutina (enum -> intensidad 1..3) a filas legibles "Pecho · Alto enfoque"
 * con un medidor de puntos. Ordena de mayor a menor enfoque para que el ojo caiga primero
 * en lo que más se trabaja.
 */
const DOT_SLOTS = ['dot-1', 'dot-2', 'dot-3', 'dot-4'];

// El medidor traduce el nivel 1..3 a puntos encendidos (2/3/4 de 4): incluso el enfoque más
// bajo enciende dos, así la fila nunca se lee como "vacía".
const litFor = (level) => Math.min(DOT_SLOTS.length, level + 1);

function FocusDots({ level }) {
  const lit = litFor(level);
  return (
    <Stack direction="row" spacing={0.5} sx={{ flex: 'none' }}>
      {DOT_SLOTS.map((slot, i) => (
        <Box
          key={slot}
          sx={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            flex: 'none',
            bgcolor: i < lit ? 'gold.main' : 'hairline.strong',
            opacity: i < lit ? 1 - i * 0.15 : 1
          }}
        />
      ))}
    </Stack>
  );
}
FocusDots.propTypes = { level: PropTypes.number.isRequired };

function MuscleFocusList({ distribution = null, selected = null, onSelect }) {
  const { t } = useTranslation(['play']);

  // Mayor enfoque primero; a igual nivel, alfabético por etiqueta para que el orden sea estable.
  const rows = useMemo(
    () =>
      Object.entries(distribution || {})
        .map(([muscle, level]) => ({ muscle, level, label: muscleLabel(t, muscle) }))
        .sort((a, b) => b.level - a.level || a.label.localeCompare(b.label)),
    [distribution, t]
  );

  if (rows.length === 0) return null;

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      {rows.map(({ muscle, level, label }) => {
        const active = selected === muscle;
        return (
          <Box
            key={muscle}
            role="button"
            tabIndex={0}
            aria-pressed={active}
            onClick={() => onSelect(muscle)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(muscle);
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: active ? 'hairline.strong' : 'hairline.main',
              bgcolor: active ? 'selected.bg' : 'surface.raised',
              transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), border-color 0.2s, background-color 0.2s',
              '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' },
              '&:active': { transform: 'scale(0.98)' }
            }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap sx={{ color: active ? 'selected.fg' : 'text.primary' }}>
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {focusLevelLabel(t, level)}
              </Typography>
            </Box>
            <FocusDots level={level} />
          </Box>
        );
      })}
    </Stack>
  );
}

MuscleFocusList.propTypes = {
  /** Distribución de la rutina: { MUSCLE_ENUM: 1|2|3 } — ver muscleDistributionOf en constants. */
  distribution: PropTypes.objectOf(PropTypes.number),
  selected: PropTypes.string,
  onSelect: PropTypes.func.isRequired
};

export default MuscleFocusList;
